import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { 
  Package2,
  Search,
  Calendar,
  FileSpreadsheet,
  Eye,
  Trash2,
  FileText,
  Truck,
  CreditCard,
  Filter,
  Banknote
} from 'lucide-react';
import { OrderStatus as PrismaOrderStatus, PaymentStatus as PrismaPaymentStatus } from '@prisma/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Card } from '../../components/ui/Card';
import { formatDate, formatPrice } from '../../utils/format';
import { Order, OrderFilters, OrdersResponse } from '../../types/orders';
import { fetchWithAuth } from '../../hooks/useAuth';

const STATUS_TRANSLATIONS: Record<PrismaOrderStatus, string> = {
  PENDING: 'În așteptare',
  PAID: 'Plătită',
  PROCESSING: 'În procesare',
  SHIPPED: 'Expediată',
  DELIVERED: 'Livrată',
  CANCELLED: 'Anulată',
};

const PAYMENT_STATUS_TRANSLATIONS: Record<PrismaPaymentStatus, string> = {
  PENDING: 'În așteptare',
  PROCESSING: 'În procesare',
  COMPLETED: 'Finalizată',
  FAILED: 'Eșuată',
  REFUNDED: 'Rambursată',
};

import { samedayStatusConfig } from '../../config/sameday.status.config';

const getStatusColor = (statusId: number): string => {
  const status = samedayStatusConfig.statusMapping[statusId];
  switch (status) {
    case 'DELIVERED': return 'bg-green-100 text-green-800';
    case 'IN_TRANSIT': return 'bg-yellow-100 text-yellow-800';
    case 'FAILED': return 'bg-red-100 text-red-800';
    case 'RETURNED': return 'bg-purple-100 text-purple-800';
    case 'CANCELED': return 'bg-red-100 text-red-800';
    case 'REGISTERED':
    default: return 'bg-blue-100 text-blue-800';
  }
};

const ORDER_STATUSES = Object.values(PrismaOrderStatus);
const PAYMENT_STATUSES = Object.values(PrismaPaymentStatus);

const paymentStatusColors: Record<PrismaPaymentStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  PROCESSING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  REFUNDED: 'bg-gray-100 text-gray-800',
};

export function OrdersPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = React.useState<OrderFilters>({
    search: '',
    status: undefined,
    paymentStatus: undefined,
    shippingStatus: undefined,
    dateFrom: '',
    dateTo: '',
  });

  // Fetch orders with filters
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(10);

  const { data: ordersResponse, isLoading } = useQuery<OrdersResponse>({
    refetchInterval: 5000, // Refetch every 5 seconds
    queryKey: ['orders', filters, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
      if (filters.shippingStatus) params.append('shippingStatus', filters.shippingStatus);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      params.append('page', page.toString());
      params.append('limit', pageSize.toString());

      const response = await fetchWithAuth(`/api/admin/orders?${params}`);
      if (!response.ok) throw new Error('Eroare la încărcarea comenzilor');
      return response.json();
    },
  });

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <Package2 className="w-8 h-8 text-red-600" />
          <h1 className="text-2xl font-bold text-gray-900">Comenzi</h1>
        </div>
        <Button 
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          onClick={async () => {
            try {
              const response = await fetchWithAuth('/api/admin/orders/export', {
                method: 'GET'
              });
              if (!response.ok) throw new Error('Eroare la exportul comenzilor');
              
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `comenzi-${formatDate(new Date())}.xlsx`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
              
              toast.success('Export realizat cu succes');
            } catch (error) {
              toast.error('Eroare la exportul comenzilor');
              console.error('Export error:', error);
            }
          }}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Export Excel
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="font-semibold text-gray-900">Filtre</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              placeholder="Caută după ID, client, email..."
              value={filters.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </div>
          
          <Select
            value={filters.status ?? 'ALL'}
            onValueChange={(value) => setFilters((f) => ({ ...f, status: value === 'ALL' ? undefined : value as PrismaOrderStatus }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Toate statusurile" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toate statusurile</SelectItem>
              {ORDER_STATUSES.map(status => (
                <SelectItem key={status} value={status}>{STATUS_TRANSLATIONS[status]}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.paymentStatus ?? 'ALL'}
            onValueChange={(value) => setFilters((f) => ({ ...f, paymentStatus: value === 'ALL' ? undefined : value as PrismaPaymentStatus }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Toate plățile" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toate plățile</SelectItem>
              {PAYMENT_STATUSES.map(status => (
                <SelectItem key={status} value={status}>{PAYMENT_STATUS_TRANSLATIONS[status]}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.shippingStatus ?? 'ALL'}
            onValueChange={(value) => setFilters((f) => ({ ...f, shippingStatus: value === 'ALL' ? undefined : value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Toate livrările" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toate livrările</SelectItem>
              {Object.entries(samedayStatusConfig.statusLabels).map(([id, label]) => (
                <SelectItem key={id} value={id}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              type="date"
              value={filters.dateFrom}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
            />
          </div>

          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              type="date"
              value={filters.dateTo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
            />
          </div>
        </div>
      </Card>

      {/* Orders Table */}
      <Card className="shadow-sm hover:shadow-md transition-shadow duration-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Plată</TableHead>
              <TableHead>Livrare</TableHead>
              <TableHead>AWB</TableHead>
              <TableHead>Factură</TableHead>
              <TableHead>Acțiuni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10">
                  Se încarcă...
                </TableCell>
              </TableRow>
            ) : ordersResponse?.orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10">
                  Nu există comenzi
                </TableCell>
              </TableRow>
            ) : (
              ordersResponse?.orders.map((order: Order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {order.id}
                      <Badge variant="outline" className={`px-2 py-0.5 flex items-center gap-1 ${
                        order.payment?.method === 'STRIPE' ? 
                        'bg-blue-50 text-blue-700 ring-1 ring-blue-700/10' : 
                        'bg-green-50 text-green-700 ring-1 ring-green-700/10'
                      }`}>
                        {order.payment?.method === 'STRIPE' ? (
                          <>
                            <CreditCard className="w-3 h-3" />
                            Card
                          </>
                        ) : (
                          <>
                            <Banknote className="w-3 h-3" />
                            Ramburs
                          </>
                        )}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(order.createdAt)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>{order.metadata?.customerName || order.user.name}</span>
                      <span className="text-sm text-gray-500">{order.metadata?.customerEmail || order.user.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>{formatPrice(order.totalPrice)}</TableCell>
                  <TableCell>
                    <Badge className={`px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset ${
                      order.status === 'CANCELLED' ? 'bg-red-100 text-red-800 ring-red-200' :
                      paymentStatusColors[order.payment?.status || 'PENDING'] + ' ring-current/20'
                    }`}>
                      {order.status === 'CANCELLED' ? 'Anulată' :
                       PAYMENT_STATUS_TRANSLATIONS[order.payment?.status || 'PENDING']}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={`px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset ${
                      order.status === 'CANCELLED' ? 'bg-red-100 text-red-800 ring-red-200' :
                      order.awbNumber ? 
                        getStatusColor(order.metadata?.shippingStatusDetails?.statusId || 1) + ' ring-current/20' :
                        'bg-yellow-100 text-yellow-800 ring-yellow-200'
                    }`}>
                      {order.status === 'CANCELLED' ? 'Anulată' :
                       order.awbNumber ? 
                        (order.metadata?.shippingStatusDetails?.statusId ? 
                          samedayStatusConfig.statusLabels[order.metadata.shippingStatusDetails.statusId] :
                          'Comandă înregistrată'
                        ) :
                        order.status === 'PENDING' ? 'În așteptare' :
                        order.status === 'PAID' ? 'Plătită' :
                        order.status === 'PROCESSING' ? 'În procesare' :
                        'În așteptare'
                      }
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {order.awbNumber ? (
                      <Button
                        variant="link"
                        className="flex items-center gap-1"
                        onClick={() => window.open(order.awbUrl, '_blank')}
                      >
                        <Truck className="w-4 h-4" />
                        {order.awbNumber}
                      </Button>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {order.invoiceNumber ? (
                      <Button
                        variant="link"
                        className="flex items-center gap-1"
                        onClick={() => window.open(order.invoiceUrl, '_blank')}
                      >
                        <FileText className="w-4 h-4" />
                        {order.invoiceSeries}/{order.invoiceNumber}
                      </Button>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="link"
                        asChild
                      >
                        <Link to={`/orders/${order.id}/`} className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          Detalii
                        </Link>
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                          if (window.confirm('Sigur doriți să anulați această comandă? Această acțiune va anula și AWB-ul și factura asociată.')) {
                            try {
                              const response = await fetchWithAuth(`/api/admin/orders/${order.id}/status`, {
                                method: 'PUT',
                                headers: {
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ status: 'CANCELLED' })
                              });
                              
                              if (!response.ok) {
                                throw new Error('Eroare la anularea comenzii');
                              }
                              
                              // Refetch orders after cancellation
                              queryClient.invalidateQueries({ queryKey: ['orders'] });
                              toast.success('Comanda a fost anulată cu succes');
                            } catch (error) {
                              toast.error('Eroare la anularea comenzii');
                              console.error('Error cancelling order:', error);
                            }
                          }
                        }}
                        disabled={order.status === 'CANCELLED'}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Anulează
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {ordersResponse && ordersResponse.total > pageSize && (
        <div className="flex justify-center mt-4">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex items-center gap-2 hover:bg-gray-100 transition-colors duration-200"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600">
                Pagina {page} din {Math.ceil(ordersResponse.total / pageSize)}
              </span>
            </div>
            <Button
              variant="outline"
              className="flex items-center gap-2 hover:bg-gray-100 transition-colors duration-200"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(ordersResponse.total / pageSize)}
            >
              Următor
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
